// src/app/(dashboard)/components/ApiStatusButton.tsx
"use client";

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface SourceStatus {
  name: string;
  description: string;
  ok: boolean;
  latencyMs: number | null;
  error: string | null;
}

export function ApiStatusButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label="สถานะระบบ"
        title="สถานะระบบ"
        onClick={() => setOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      >
        <GearIcon />
      </button>
      {open && <ApiStatusModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ApiStatusModal({ onClose }: { onClose: () => void }) {
  const [sources, setSources] = useState<SourceStatus[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/health/sources", { cache: "no-store" });
      const json = await res.json();
      setSources(json.sources ?? []);
    } catch {
      setSources([]);
    } finally {
      setCheckedAt(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="สถานะระบบ"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
            สถานะระบบ
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="ปิด"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {loading && !sources ? (
            <div className="flex items-center gap-2 py-4 text-sm text-slate-500 dark:text-slate-400">
              <Spinner />
              กำลังตรวจสอบ...
            </div>
          ) : (
            sources?.map((s) => (
              <div
                key={s.name}
                className="rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "h-2.5 w-2.5 shrink-0 rounded-full",
                        s.ok ? "bg-emerald-500" : "bg-rose-500",
                      ].join(" ")}
                    />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {s.name}
                    </span>
                  </div>
                  <span
                    className={[
                      "shrink-0 text-xs font-medium",
                      s.ok
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400",
                    ].join(" ")}
                  >
                    {s.ok
                      ? `ทำงานปกติ${s.latencyMs != null ? ` · ${s.latencyMs}ms` : ""}`
                      : "ใช้งานไม่ได้"}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                  {s.description}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {checkedAt
              ? `ตรวจสอบล่าสุด: ${checkedAt.toLocaleTimeString("th-TH")}`
              : ""}
          </span>
          <button
            type="button"
            onClick={check}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {loading && <Spinner />}
            ตรวจสอบอีกครั้ง
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function GearIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
